import { useEffect, useRef } from 'react';
import { forceRefresh } from '@/shared/api/client';
import { useAuthStore } from '@/shared/lib/auth/store';
import { subscribeStream } from './client';
import type { SseEvent } from './parser';

/* React binding for the admin SSE feed. Subscribes ONCE on mount and tears
   down on unmount. The handler is read through a ref, so a changing `onEvent`
   identity — e.g. React StrictMode's double-mount, or a caller that forgot to
   memoize — never tears the stream down and reopens it, which would drop the
   Last-Event-ID replay cursor and reconnect needlessly. */

export function useAdminEventStream(
  onEvent: (ev: SseEvent) => void,
  onOpen?: () => void,
): void {
  const handlerRef = useRef(onEvent);
  const onOpenRef = useRef(onOpen);
  useEffect(() => {
    handlerRef.current = onEvent;
    onOpenRef.current = onOpen;
  }, [onEvent, onOpen]);

  useEffect(() => {
    const dispose = subscribeStream({
      path: '/v1/admin/stream',
      getToken: () => useAuthStore.getState().accessToken,
      onUnauthorized: forceRefresh,
      // Persistent refresh failure => the session is dead; clear + redirect
      // (mirrors the apiRequest path) instead of sitting on a silently-dead page.
      onAuthInvalidated: () => useAuthStore.getState().reset(),
      onEvent: (ev) => handlerRef.current(ev),
      onOpen: () => onOpenRef.current?.(),
    });
    return dispose;
  }, []);
}
